import { useState } from "react";
import { HeartHandshake, Lightbulb, Flag, Trash2 } from "lucide-react";
import { HandleAvatar } from "@/components/HandleAvatar";
import { SampleTag } from "@/components/community/SampleTag";

const FLAG_REASONS = [["advice_not_wanted", "Unwanted advice"], ["unkind", "Unkind"], ["off_topic", "Off topic"], ["other", "Other"]];

export function PostComposer({ accepts, memberHandle, onSend }) {
  const [kind, setKind] = useState("support");
  const [text, setText] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    if (await onSend(kind, text)) setText("");
  };

  return (
    <form onSubmit={submit} className="space-y-2">
      <div className="flex gap-2">
        <button type="button" data-testid="post-kind-support" onClick={() => setKind("support")}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${kind === "support" ? "bg-emerald-700 text-white" : "bg-stone-100 text-stone-700"}`}>
          <HeartHandshake className="w-3.5 h-3.5" /> Support
        </button>
        <button type="button" data-testid="post-kind-suggestion" disabled={!accepts} onClick={() => setKind("suggestion")}
          title={accepts ? "" : `${memberHandle} hasn't opted in to suggestions`}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed ${kind === "suggestion" ? "bg-amber-600 text-white" : "bg-stone-100 text-stone-700"}`}>
          <Lightbulb className="w-3.5 h-3.5" /> Suggestion
        </button>
      </div>
      <textarea data-testid="post-text" value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={500}
        placeholder={kind === "support" ? "Cheer them on — encouragement only, no advice." : "Share a gentle, practical idea."}
        className="w-full px-3 py-2 rounded-xl border border-stone-300 text-sm focus:border-amber-600 focus:ring-2 focus:ring-amber-200 outline-none" />
      <button data-testid="post-send" type="submit" className="amber-btn text-sm">Send</button>
    </form>
  );
}

export function WallPost({ post, onFlag, onDelete }) {
  const [flagging, setFlagging] = useState(false);
  return (
    <li data-testid={`post-${post.id}`} className="rounded-2xl border border-stone-100 p-3 text-sm">
      <div className="flex items-center justify-between gap-2">
        <span className={`text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-1.5 ${post.kind === "support" ? "text-emerald-800" : "text-amber-800"}`}>
          <HandleAvatar handle={post.from_name} size="sm" /> {post.kind} · {post.from_name}{post.is_sample && <SampleTag id={`wall-post-${post.id}`} />}
        </span>
        {post.is_mine ? (
          <button data-testid={`post-delete-${post.id}`} onClick={() => onDelete(post.id)} className="text-stone-400 hover:text-stone-700"><Trash2 className="w-3.5 h-3.5" /></button>
        ) : (
          <button data-testid={`post-flag-${post.id}`} onClick={() => setFlagging((f) => !f)} className={`inline-flex items-center gap-1 text-xs ${post.flagged_by_me ? "text-red-700" : "text-stone-400 hover:text-red-700"}`}>
            <Flag className="w-3.5 h-3.5" /> {post.flagged_by_me ? "Flagged" : "Flag"}
          </button>
        )}
      </div>
      <p className="text-stone-800 mt-1">{post.text}</p>
      {flagging && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {FLAG_REASONS.map(([k, l]) => (
            <button key={k} data-testid={`flag-reason-${k}-${post.id}`} onClick={() => { setFlagging(false); onFlag(post.id, k); }} className="px-2.5 py-1 rounded-full text-xs bg-stone-100 hover:bg-red-100 text-stone-700">{l}</button>
          ))}
        </div>
      )}
    </li>
  );
}

export function WallHabits({ habits, accepts, isMe }) {
  if (!habits?.length) return null;
  return (
    <div className="rounded-2xl bg-stone-50 p-3" data-testid="wall-habits">
      <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-1">Working on</div>
      <div className="flex flex-wrap gap-1">
        {habits.map((h) => (
          <span key={`${h.kind}-${h.title}`} className={`text-xs px-2 py-0.5 rounded-full ${h.kind === "stop" ? "bg-rose-50 text-rose-800" : "bg-emerald-50 text-emerald-800"}`}>{h.kind === "stop" ? "Stop: " : ""}{h.title}</span>
        ))}
      </div>
      {accepts && !isMe && <div className="text-[11px] text-stone-500 mt-1.5">They're open to ideas — anything that helped you with something similar is welcome.</div>}
    </div>
  );
}
